import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { LanguageToggle } from '@/components/ui/language-toggle'
import {
  Bell,
  Search,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Volume2,
  VolumeX,
  Loader2,
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/components/auth'
import { useAdminAlert } from '@/hooks/use-admin-alert'
import { AdminAlertService } from '@/domain/admin-alert/admin-alert.service'
import type { AdminAlert } from '@/domain/admin-alert/admin-alert.types'

interface HeaderProps {
  collapsed: boolean
}

export function Header({ collapsed }: HeaderProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, logout, sessionToken } = useAuth()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  
  // ✅ Admin Alert hook (แยกจาก Member Notification)
  const {
    alerts,
    counts,
    isLoading: isLoadingAlerts,
    soundEnabled,
    setSoundEnabled,
    testSound,
  } = useAdminAlert({
    enabled: !!sessionToken, // ✅ เปิดใช้งานเมื่อมี session token
    interval: 30000, // 30 seconds
    soundEnabled: true,
  })

  // Debug: ตรวจสอบ alerts state
  console.log("🔔 [Header] Alerts state:", {
    alertsCount: alerts.length,
    counts,
    isLoading: isLoadingAlerts,
    alerts: alerts.map((a) => ({ id: a.id, type: a.type, title: a.title })),
  })
  
  // Logout handler
  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await logout()
    } finally {
      setIsLoggingOut(false)
    }
  }
  
  // Get user initials
  const getUserInitials = () => {
    if (!user) return 'SA'
    const first = user.firstName?.[0] || ''
    const last = user.lastName?.[0] || ''
    return (first + last).toUpperCase() || user.username?.slice(0, 2).toUpperCase() || 'SA'
  }
  
  // Get display name
  const getDisplayName = () => {
    if (!user) return 'Super Admin'
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }
    return user.username
  }

  // Handle admin alert click
  const handleAlertClick = async (alert: AdminAlert) => {
    // ✅ Mark as viewed
    if (sessionToken) {
      try {
        if (alert.type === 'member' && alert.memberId) {
          await AdminAlertService.markAlertAsViewed(
            sessionToken,
            'member',
            alert.memberId,
            'Member'
          )
        } else if (alert.type === 'deposit' && alert.transactionId) {
          await AdminAlertService.markAlertAsViewed(
            sessionToken,
            'deposit',
            alert.transactionId,
            'DepositWithdraw'
          )
        } else if (alert.type === 'withdraw' && alert.transactionId) {
          await AdminAlertService.markAlertAsViewed(
            sessionToken,
            'withdraw',
            alert.transactionId,
            'DepositWithdraw'
          )
        }
      } catch (error) {
        console.error('❌ [Header] Error marking alert as viewed:', error)
        // Continue navigation even if mark as viewed fails
      }
    }

    // Navigate (รองรับ actionUrl)
    if (alert.actionUrl) {
      navigate(alert.actionUrl)
      return
    }

    // Default navigation
    if (alert.type === 'deposit' || alert.type === 'withdraw') {
      // ✅ ไปที่ Smart Cashier และ highlight transaction (ถ้ามี transactionId)
      if (alert.transactionId) {
        navigate(`/transactions/smart-cashier?highlight=${alert.transactionId}`)
      } else {
        navigate(`/transactions/smart-cashier`)
      }
    } else if (alert.type === 'member') {
      // ✅ ไปที่หน้า Members และเปิด Member Detail Dialog (ถ้ามี memberId)
      if (alert.memberId) {
        navigate(`/members?memberId=${alert.memberId}`)
      } else {
        navigate(`/members`)
      }
    }
  }

  return (
    <header
      className={cn(
        "fixed top-0 right-0 z-30 flex h-16 items-center justify-between border-b border-dynamic bg-background/95 backdrop-blur-sm px-6 transition-all duration-300",
        collapsed ? "left-16" : "left-64"
      )}
    >
      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('header.search')}
            className="h-9 w-64 rounded-lg border bg-muted/50 pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-1">
        {/* Sound Toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const newValue = !soundEnabled
                setSoundEnabled(newValue)
                console.log(`🔊 [Header] Sound ${newValue ? 'enabled' : 'disabled'}`)
                // ทดสอบเสียงเมื่อเปิด
                if (newValue && testSound) {
                  setTimeout(() => {
                    testSound()
                  }, 100)
                }
              }}
            >
              {soundEnabled ? (
                <Volume2 className="h-5 w-5" />
              ) : (
                <VolumeX className="h-5 w-5 text-muted-foreground" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {soundEnabled ? t('header.soundOff') : t('header.soundOn')}
          </TooltipContent>
        </Tooltip>

        {/* Theme Toggle (Color + Mode) */}
        <ThemeToggle />

        {/* Language Toggle (Flag Only) */}
        <LanguageToggle />

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              {counts.total > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                  {counts.total > 99 ? '99+' : counts.total}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>{t('header.notifications')}</span>
              {counts.total > 0 && (
                <Badge variant="secondary">{counts.total} {t('header.new')}</Badge>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isLoadingAlerts ? (
              <DropdownMenuItem disabled className="justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('common.loading')}
              </DropdownMenuItem>
            ) : alerts.length === 0 ? (
              <DropdownMenuItem disabled className="justify-center py-4 text-muted-foreground">
                {t('header.noNotifications')}
              </DropdownMenuItem>
            ) : (
              <>
                {alerts.map((alert) => (
                  <DropdownMenuItem
                    key={alert.id}
                    className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                    onClick={() => handleAlertClick(alert)}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "h-2 w-2 rounded-full",
                          alert.type === 'deposit' && "bg-win",
                          alert.type === 'withdraw' && "bg-loss",
                          alert.type === 'member' && "bg-primary"
                        )}
                      />
                      <span className="font-medium">{alert.title}</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-4">
                      {alert.description}
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-4">
                      {alert.timeAgo}
                    </span>
                  </DropdownMenuItem>
                ))}
            <DropdownMenuSeparator />
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuItem 
                className="flex-1 justify-center text-primary cursor-pointer"
                onClick={() => navigate('/transactions/smart-cashier')}
              >
                {t('header.viewAll')}
              </DropdownMenuItem>
              {testSound && (
                <DropdownMenuItem 
                  className="text-xs text-muted-foreground cursor-pointer px-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    console.log('🧪 [Header] Testing sound manually...')
                    testSound()
                  }}
                  title="ทดสอบเสียง"
                >
                  🔊
                </DropdownMenuItem>
              )}
            </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="mx-2 h-8 w-px bg-border" />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 px-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.photo || '/avatar.png'} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium">{getDisplayName()}</span>
                <span className="text-xs text-muted-foreground">
                  {user?.email || user?.username || 'admin'}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{t('header.myAccount')}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {user?.type === 'super_admin' ? 'Super Admin' : 'Admin'}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
              <User className="mr-2 h-4 w-4" />
              {t('header.profile')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
              <Settings className="mr-2 h-4 w-4" />
              {t('header.settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive cursor-pointer"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="mr-2 h-4 w-4" />
              )}
              {isLoggingOut ? 'กำลังออก...' : t('header.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
